import type { Difficulty, LevelDefinition } from "@/domain";

import { createFocusSocialLevel } from "./create-level";

export interface DirectionTargetDefinition {
  id: string;
  label: string;
}

export interface DirectionStepDefinition {
  id: string;
  order: number;
  action: "select";
  targetId: string;
  caption: string;
  audio: string;
}

export interface FollowDirectionsPayload {
  targets: DirectionTargetDefinition[];
  steps: DirectionStepDefinition[];
}

type DirectionStepInput = Omit<DirectionStepDefinition, "audio">;

function withDirectionAudio(
  difficulty: Difficulty,
  payload: Omit<FollowDirectionsPayload, "steps"> & {
    steps: DirectionStepInput[];
  }
): FollowDirectionsPayload {
  return {
    ...payload,
    steps: payload.steps.map((step) => ({
      ...step,
      audio:
        "/audio/focus-social/follow-directions-" +
        String(difficulty) +
        "-" +
        step.id +
        ".ogg"
    }))
  };
}

export const followDirectionsLevels: LevelDefinition<FollowDirectionsPayload>[] = [
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 1,
      title: "点一点小铃铛",
      instruction: "叮铃铃，小铃铛在等你。请点一下它。",
      objectives: ["听懂并完成一个简单动作", "把听到的物品与画面对应起来"]
    },
    withDirectionAudio(1, {
      targets: [
        { id: "bell", label: "小铃铛" },
        { id: "apple", label: "苹果" },
        { id: "sun", label: "太阳" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "bell",
          caption: "第一步，点一下小铃铛。"
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 2,
      title: "太阳和云朵",
      instruction: "太阳和云朵来排队。先点太阳，再点云朵；忘记了，可以再听一次。",
      objectives: ["记住并完成两个有顺序的动作", "理解先做和再做"]
    },
    withDirectionAudio(2, {
      targets: [
        { id: "sun", label: "太阳" },
        { id: "cloud", label: "云朵" },
        { id: "moon", label: "月亮" },
        { id: "star", label: "星星" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "sun",
          caption: "先来点太阳。"
        },
        {
          id: "step-2",
          order: 2,
          action: "select",
          targetId: "cloud",
          caption: "接着点云朵。"
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 3,
      title: "颜色和形状",
      instruction: "形状也来排队啦。先点红色圆形，再点蓝色三角形。",
      objectives: ["在相似图形中听辨两个特征", "保持两个动作的先后顺序"]
    },
    withDirectionAudio(3, {
      targets: [
        { id: "red-circle", label: "红色圆形" },
        { id: "blue-triangle", label: "蓝色三角形" },
        { id: "blue-circle", label: "蓝色圆形" },
        { id: "red-triangle", label: "红色三角形" },
        { id: "yellow-square", label: "黄色方形" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "red-circle",
          caption: "先来点红色圆形。"
        },
        {
          id: "step-2",
          order: 2,
          action: "select",
          targetId: "blue-triangle",
          caption: "接着点蓝色三角形。"
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 4,
      title: "准备上学啦",
      instruction: "文具排好队了。先点铅笔，再点图画书，最后点书包。",
      objectives: ["记住三个连续动作", "按口头指令依次选择学习用品"]
    },
    withDirectionAudio(4, {
      targets: [
        { id: "pencil", label: "铅笔" },
        { id: "picture-book", label: "图画书" },
        { id: "backpack", label: "书包" },
        { id: "ruler", label: "尺子" },
        { id: "eraser", label: "橡皮" },
        { id: "lunch-box", label: "餐盒" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "pencil",
          caption: "先来点铅笔。"
        },
        {
          id: "step-2",
          order: 2,
          action: "select",
          targetId: "picture-book",
          caption: "接着点图画书。"
        },
        {
          id: "step-3",
          order: 3,
          action: "select",
          targetId: "backpack",
          caption: "最后点书包。"
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 5,
      title: "听清三个特征",
      instruction: "三块形状排好队了。先点小黄色星形，再点大蓝色圆形，最后点小绿色方形。",
      objectives: ["记住三个带有视觉特征的动作", "在更多干扰项中按顺序选择"]
    },
    withDirectionAudio(5, {
      targets: [
        { id: "small-yellow-star", label: "小黄色星形" },
        { id: "large-blue-circle", label: "大蓝色圆形" },
        { id: "small-green-square", label: "小绿色方形" },
        { id: "large-yellow-star", label: "大黄色星形" },
        { id: "small-blue-circle", label: "小蓝色圆形" },
        { id: "large-green-square", label: "大绿色方形" },
        { id: "small-red-square", label: "小红色方形" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "small-yellow-star",
          caption: "先点小黄色星形。"
        },
        {
          id: "step-2",
          order: 2,
          action: "select",
          targetId: "large-blue-circle",
          caption: "再点大蓝色圆形。"
        },
        {
          id: "step-3",
          order: 3,
          action: "select",
          targetId: "small-green-square",
          caption: "最后点小绿色方形。"
        }
      ]
    })
  ),
  createFocusSocialLevel(
    "follow-directions",
    {
      difficulty: 6,
      title: "教室里的四步任务",
      instruction: "准备好，跟着点点做四步。先点门边的雨伞，再点窗边的绿植。接着点墙上的时钟，最后点椅子上的书包。",
      objectives: ["记住并完成四个连续动作", "在完整场景中结合位置和物品信息"]
    },
    withDirectionAudio(6, {
      targets: [
        { id: "umbrella-by-door", label: "门边的雨伞" },
        { id: "plant-by-window", label: "窗边的绿植" },
        { id: "clock-on-wall", label: "墙上的时钟" },
        { id: "backpack-on-chair", label: "椅子上的书包" },
        { id: "ball-under-table", label: "桌子下的皮球" },
        { id: "book-on-desk", label: "课桌上的图画书" },
        { id: "coat-by-window", label: "窗边的外套" },
        { id: "lunch-box-on-chair", label: "椅子上的餐盒" }
      ],
      steps: [
        {
          id: "step-1",
          order: 1,
          action: "select",
          targetId: "umbrella-by-door",
          caption: "先点门边的雨伞。"
        },
        {
          id: "step-2",
          order: 2,
          action: "select",
          targetId: "plant-by-window",
          caption: "再点窗边的绿植。"
        },
        {
          id: "step-3",
          order: 3,
          action: "select",
          targetId: "clock-on-wall",
          caption: "然后点墙上的时钟。"
        },
        {
          id: "step-4",
          order: 4,
          action: "select",
          targetId: "backpack-on-chair",
          caption: "最后点椅子上的书包。"
        }
      ]
    })
  )
];
